import { useState, useEffect, useMemo, createContext } from "react";
import { useAccount } from "@azure/msal-react";
import {
  readDataFromLocalStorage,
  generateEntityKeyForLocalStorage,
  saveDataToLocalStorage,
} from "./utils/table";

const GlobalContext = createContext();

// This provider is intended to be surrounding the whole application.
// It should receive the users permissions as parameter
const GlobalContextProvider = ({ children }) => {

  // Migrate all _oasis_*_columnOrder keys to array of field names if needed
  useEffect(() => {
    // Find all keys in localStorage matching _oasis_*_columnOrder
    Object.keys(localStorage)
      .filter(key => /^_oasis_.*-columnOrder$/.test(key))
      .forEach(storageKey => {
        const originalData = readDataFromLocalStorage(storageKey);
        // Only migrate if it's an array of objects (not already array of strings)
        if (
          Array.isArray(originalData) &&
          originalData.length > 0 &&
          typeof originalData[0] === 'object' &&
          originalData[0] !== null &&
          'field' in originalData[0]
        ) {
          const fieldNames = originalData.map(col => col.field);
          localStorage.setItem(storageKey, JSON.stringify(fieldNames));
        }
      });
  }, []); // Empty dependency array ensures this runs once at startup

  const [user, setUser] = useState({
    name: "Anonymous User",
    username: "Anonymous",
  });

  const [globalFilters, setGlobalFilters] = useState(() => {
    const cachedGlobalFilter = readDataFromLocalStorage(
      generateEntityKeyForLocalStorage("object", "global")
    );
    return cachedGlobalFilter || null;
  });

  const [objectCreatedCount, setObjectCreatedCount] = useState(0);

  // Get the account information from the useAccount hook
  // @docs: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md#useaccount-hook
  const accountInfo = useAccount();

  useEffect(() => {
    if (accountInfo) {
      setUser({
        name: accountInfo.name ?? "",
        username: accountInfo.idTokenClaims?.email ?? "",
      });
    }
  }, [accountInfo]);

  const setGlobalObjectFilter = (filter) => {
    setGlobalFilters(filter);
    if (filter) {
      saveDataToLocalStorage(
        generateEntityKeyForLocalStorage("object", "global"),
        filter
      );
    }
  };

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated: accountInfo !== null,
      globalFilters,
      setGlobalObjectFilter,
      objectCreatedCount,
      setObjectCreatedCount,
    }),
    [user, accountInfo, globalFilters, objectCreatedCount]
  );

  // This component will render its children
  // wrapped around a UserContext's provider whose
  // value is set to the method defined above
  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};

export { GlobalContextProvider, GlobalContext };
